<script>
import { defineAsyncComponent } from 'vue'
import { authComputed, userComputed } from '@state/helpers'
import NavBar from '@components/nav-bar'
import AppFooter from '@components/app-footer'
import AppNotify from '@components/app-notify'
import LoadingIndicator from '@components/LoadingIndicator'
import BottomNavigation from '@components/bottom-navigation'
import NotificationBell from '@components/notification-bell'
import LanguageToggle from '@components/language-toggle'
import ThemeToggle from '@components/theme-toggle'
import OfflinePendingChip from '@components/offline-pending-chip'
import {
  ASK_BOWVUE_ENABLED,
  LEGACY_INFRARED_ENABLED,
} from '@utils/feature-flags'
import { chatMethods } from '@state/helpers'
import { mayOpenAdminArea } from '@utils/admin-area'
import { ASK_BOWVUE_AVAILABLE_QUERY } from '@src/graphql/queries'
export default {
  components: {
    NavBar,
    AppFooter,
    AppNotify,
    BottomNavigation,
    LoadingIndicator,
    NotificationBell,
    LanguageToggle,
    ThemeToggle,
    OfflinePendingChip,
    // Ask BowVue is flag-gated (see utils/feature-flags). The conditional
    // spread keeps the dynamic import out of disabled builds entirely.
    // Rollup constant-folds the flag and drops the chat chunks from dist.
    // The disabled branch registers a render-nothing stub so the template
    // reference stays resolvable (no dev-mode "failed to resolve" warning).
    ChatWidget: ASK_BOWVUE_ENABLED
      ? defineAsyncComponent(() => import('@components/chat-widget'))
      : { render: () => null },
  },
  data: () => ({
    askBowvueEnabled: ASK_BOWVUE_ENABLED,
    // Legacy Infrared archive. Gated with its route so the toolbar entry
    // cannot outlive it (see utils/feature-flags).
    legacyInfraredEnabled: LEGACY_INFRARED_ENABLED,
    // Whether THIS user may use askBowvue: admins and developers, plus users at or
    // beneath an allow-listed customer. Answered, and enforced, by the backend;
    // this only decides whether to render the control.
    //
    // Defaults to FALSE so the widget never flashes into view while the query is in
    // flight and then vanishes for a user who was never entitled to it.
    askBowvueAvailable: false,
    drawer: false,
  }),
  computed: {
    // ...authLinks,
    ...authComputed,
    ...userComputed,
    // isAdmin and can come from the `authComputed` + `userComputed` spreads
    // above (the auth module's getters). They used to be re-implemented here;
    // see src/utils/roles.js for why they no longer are.
    //
    // Whoever may open a page of the admin area (src/utils/admin-area.js).
    showAdmin() {
      return mayOpenAdminArea(this.can, this.isAdmin)
    },
    // Internal @bowtie8.com staff. The only users who can be scheduled, and so
    // the only ones who receive notifications. Used to gate the bell.
    isInternalUser() {
      return (
        typeof this.email === 'string' &&
        this.email.toLowerCase().endsWith('@bowtie8.com')
      )
    },
  },
  watch: {
    // Mirror entitlement into the chat store so the dashboard's ask bar can gate on
    // it without running a second askBowvueAvailable query. The layout stays the
    // single owner of the request; the store is just where the answer is readable
    // from.
    askBowvueAvailable: {
      immediate: true,
      handler(value) {
        this.setAvailable(value)
      },
    },
  },
  methods: {
    ...chatMethods,
  },
  apollo: {
    askBowvueAvailable: {
      query: ASK_BOWVUE_AVAILABLE_QUERY,
      // Skipped when it cannot change anything: asking while signed out is a
      // guaranteed failure, and asking in a build with the widget compiled out is
      // pure waste.
      skip() {
        return !ASK_BOWVUE_ENABLED || !this.loggedIn
      },
      // A failed lookup must leave the widget HIDDEN rather than falling back to
      // shown. Availability is not the security boundary, askBowvue enforces that
      // itself, but a widget that refuses every question is worse than no widget.
      error() {
        this.askBowvueAvailable = false
      },
    },
  },
}
</script>

<template>
  <v-app>
    <NavBar v-model:drawer="drawer" />
    <v-app-bar color="brandSurface" theme="dark" app>
      <v-app-bar-nav-icon
        v-if="isMobileDevice"
        @click.stop="drawer = !drawer"
      ></v-app-bar-nav-icon>
      <v-toolbar-items v-if="!isMobileDevice" class="nav-toolbar-items">
        <v-btn to="/" color="white" size="small" variant="text">{{
          $t('nav.home')
        }}</v-btn>
        <v-btn
          v-if="loggedIn"
          to="/dashboard"
          size="small"
          color="white"
          variant="text"
          >{{ $t('nav.dashboard') }}</v-btn
        >
        <v-btn
          v-if="loggedIn"
          to="/locations"
          color="white"
          size="small"
          variant="text"
          >{{ $t('nav.locations') }}</v-btn
        >
        <v-btn
          v-if="loggedIn"
          href="/pdfs/moves-adds-changes.pdf"
          download="Moves_Adds_Changes.pdf"
          target="_blank"
          rel="noopener noreferrer"
          color="white"
          size="small"
          variant="text"
          >{{ $t('nav.movesAddsChanges') }}</v-btn
        >
        <v-btn
          v-if="loggedIn"
          :to="{ name: 'how-to' }"
          size="small"
          color="white"
          variant="text"
          >{{ $t('nav.howTo') }}</v-btn
        >
        <!-- The field workspace, for whoever records surveys. Mirrored in the
             drawer (nav-bar.vue). -->
        <v-btn
          v-if="can('infrared.write')"
          to="/infrared"
          size="small"
          color="white"
          variant="text"
          >{{ $t('nav.infrared') }}</v-btn
        >
        <!-- Whoever reads LOTO, as the route (it was admins only for the
             initial rollout). Mirrored in the drawer (nav-bar.vue). -->
        <v-btn
          v-if="can('loto.read')"
          :to="{ name: 'loto' }"
          size="small"
          color="white"
          variant="text"
          >{{ $t('nav.loto') }}</v-btn
        >
        <!-- Read-only archive from the retired infrared system. infrared.legacy,
             as the route and every legacyIR* query, and flag-gated alongside
             the route (see utils/feature-flags). Mirrored in the drawer
             (nav-bar.vue). This toolbar is the desktop-only surface. -->
        <v-btn
          v-if="legacyInfraredEnabled && can('infrared.legacy')"
          :to="{ name: 'legacy-infrared' }"
          size="small"
          color="white"
          variant="text"
          >{{ $t('nav.legacyInfrared') }}</v-btn
        >
        <v-btn
          v-if="showAdmin"
          :to="{ name: 'admin' }"
          size="small"
          color="white"
          variant="text"
          >{{ $t('nav.admin') }}</v-btn
        >
      </v-toolbar-items>
      <v-spacer />
      <!-- Offline status: pending offline-change count + connectivity. -->
      <OfflinePendingChip v-if="loggedIn" />
      <!-- Notifications are currently schedule-only, and only internal
           @bowtie8.com staff can be scheduled, so gate the bell to them. Widen
           this when notifications cover features that reach other users. -->
      <NotificationBell v-if="loggedIn && isInternalUser" />
      <ThemeToggle />
      <LanguageToggle />
      <v-toolbar-items>
        <v-btn
          v-if="loggedIn"
          :to="{ name: 'profile' }"
          variant="text"
          :title="$t('nav.profile')"
        >
          <v-icon>$account</v-icon>
        </v-btn>
        <v-btn v-if="loggedIn" :to="{ name: 'logout' }" variant="text">{{
          $t('common.logOut')
        }}</v-btn>
        <v-btn v-else :to="{ name: 'login' }" variant="text">{{
          $t('common.logIn')
        }}</v-btn>
      </v-toolbar-items>
    </v-app-bar>
    <v-main>
      <LoadingIndicator />
      <v-container fluid>
        <AppNotify />
        <slot />
      </v-container>
    </v-main>
    <AppFooter
      v-if="loggedIn"
      :links="[
        { name: 'terms', title: $t('common.terms') },
        { name: 'contact us', title: $t('common.contactUs') },
      ]"
    />
    <AppFooter
      v-else
      :links="[
        { name: 'terms', title: $t('common.terms') },
        { name: 'contact us', title: $t('common.contactUs') },
      ]"
    />
    <BottomNavigation />
    <ChatWidget v-if="askBowvueEnabled && loggedIn && askBowvueAvailable" />
  </v-app>
</template>

<style>
.nav-toolbar-items {
  /* !important: matches the original inline :style binding's effective
     specificity against Vuetify's own dynamic z-index handling. */
  z-index: 9999999 !important;
}
</style>
